import React from 'react';
import { Edit3, ToggleLeft, ToggleRight, Loader2, Info, Eye, EyeOff } from 'lucide-react';
import { Modal } from './WhatsappConfigComponents';

const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const DetailRow = ({ label, value, className = '' }) => (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <p className="text-white/60 text-xs uppercase tracking-wider mb-2">{label}</p>
        <p className={`font-medium text-white text-sm break-all ${className}`}>{value || '—'}</p>
    </div>
);

export default function WhatsappDetailsModal({
    isOpen, onClose,
    config,
    showPassword, onTogglePassword,
    isUpdating, configToUpdate,
    onEdit, onToggleStatus,
}) {
    if (!config) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`${config.appName} Details`}>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DetailRow label="App Name" value={config.appName} />
                    <DetailRow label="WhatsApp URL" value={config.whatsappUrl} />
                    <DetailRow label="Vendor Name" value={config.whatsapp_vendor_name} />
                    <DetailRow label="Username" value={config.username} />
                    <DetailRow label="Configuration Type" value={config.whatsapp_config_type} />
                    <DetailRow label="Creation Date" value={formatTimestamp(config.creation_time_str)} />
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Status</p>
                        <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${config.STATUS === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'}`} />
                            <span className={`font-medium ${config.STATUS === 'ACTIVE' ? 'text-green-300' : 'text-red-300'}`}>{config.STATUS}</span>
                        </div>
                    </div>
                    <DetailRow label="Manager Email" value={config.notification_email} />
                </div>

                {/* Password row */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-white/60 text-xs uppercase tracking-wider">Password</p>
                        <button onClick={onTogglePassword} className="flex items-center space-x-1 px-2 py-1 bg-white/10 hover:bg-white/20 border border-white/10 rounded text-white/80 transition-all text-sm">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            <span>{showPassword ? 'Hide' : 'Show'}</span>
                        </button>
                    </div>
                    <div className="bg-slate-900/50 border border-white/10 rounded p-3">
                        <p className="font-mono text-sm text-white/80 break-all">
                            {showPassword ? config.wa_password : '••••••••••••••••'}
                        </p>
                    </div>
                </div>

                {config.UPDATE_TIME && <DetailRow label="Last Updated" value={formatTimestamp(config.UPDATE_TIME)} />}

                {/* Action Buttons */}
                <div className="flex items-center gap-3 pt-2">
                    <button
                        onClick={() => { onEdit(config); onClose(); }}
                        disabled={config.STATUS !== 'ACTIVE'}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${config.STATUS === 'ACTIVE' ? 'bg-blue-600/80 hover:bg-blue-600 text-white' : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'}`}
                    >
                        <Edit3 className="w-4 h-4" />
                        <span>Edit</span>
                    </button>
                    <button
                        onClick={() => onToggleStatus(config)}
                        disabled={isUpdating}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all disabled:opacity-50 ${config.STATUS === 'ACTIVE' ? 'bg-green-600/80 hover:bg-green-600 text-white' : 'bg-red-600/80 hover:bg-red-600 text-white'}`}
                    >
                        {isUpdating && configToUpdate?._id === config._id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : config.STATUS === 'ACTIVE' ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />
                        }
                        <span>{config.STATUS === 'ACTIVE' ? 'Active' : 'Inactive'}</span>
                    </button>
                </div>

                {config.STATUS !== 'ACTIVE' && (
                    <div className="flex items-start space-x-2 text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-3">
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="text-xs">Editing is only available when configuration is active.</span>
                    </div>
                )}
            </div>
        </Modal>
    );
}